import { useEffect, useState } from "react";
import { Box, Button, Checkbox, CircularProgress, Dialog, FormControlLabel, Tooltip, makeStyles } from "@material-ui/core";
import DateInputField, { getCurrentDate } from "../ui/DateInputField";
import { OrderStatus } from "../../api/OrderApi";
import { AuditableNamedIdentifiable, Identifiable, ItemCompletedOrderRecord, NamedIdentifiable } from "../../model/Items";
import { ItemOrderedKit } from "../../api/OrderApi";

interface Props<T> {
  customerId: number;
  open: boolean;
  onCleanupConfirmed: (messages: string[]) => void;
  onCleanupCanceled: () => void;
  fetchItems: (customerId: number) => Promise<AuditableNamedIdentifiable[]>;
  fetchCompletedOrderItems: (customerId: number) => Promise<CompletedOrderItem[]>;
  fetchActiveOrderedItems: (customerId: number) => Promise<OrderedItem[]>;
  fetchActiveOrderedKitItems: (customerId: number) => Promise<OrderedKitItem[]>;
  deleteItem: (customerId: number, itemId: number) => Promise<NamedIdentifiable>;
  messages: {
    itemDeletedSuccessfully: string,
    failedToDeleteItem: string,
    used: string,
  }
}

interface RemovableItem {
  id: number;
  name: string;
  latestOrderCompletionDate: string|null;
  isUsedInAnyOrder: boolean;
  kitNames: string[];
  orderNumbers: string[];
  modificationTime: string|null;
}

export interface CompletedOrderItem
extends ItemCompletedOrderRecord {
  itemId: number;
}

export interface OrderedItem {
  item: Identifiable;
}

export interface OrderedKitItem
extends ItemOrderedKit {
  item: Identifiable;
}

const useStyles = makeStyles(theme => ({
  button: {
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    minWidth: 640
  }
}));

const MINIMUM_MODIFICATION_DATE = '2023-10-12';

const formatDate = (time: string) => time.substring(0, 10);

const getId = (item: Identifiable) => item.id;

const OrderItemCleanupDialog = <T,>(props: Props<T>) => {
  const classes = useStyles();
  const [isRemovalInProgress, setRemovalInProgress] = useState<boolean>(false);
  const [latestOrderCompletionDate, setLatestOrderCompletionDate] = useState<string>();
  const [latestItemModificationDate, setLatestItemModificationDate] = useState<string>();
  const [items, setItems] = useState<AuditableNamedIdentifiable[]>();
  const [completedOrderItems, setCompletedOrderItems] = useState<CompletedOrderItem[]>();
  const [activeOrderedItems, setActiveOrderedItems] = useState<OrderedItem[]>();
  const [activeOrderedKitItems, setActiveOrderedKitItems] = useState<OrderedKitItem[]>();
  const [removableItems, setRemovableItems] = useState<RemovableItem[]>();
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>();
  const customerId = props.customerId;
  const messageConfiguration = props.messages;

  const handleItemSelected = (selectedItemId: number, checked: boolean) => {
    setSelectedItemIds(
      checked === true
        ? selectedItemIds?.concat(selectedItemId)
        : selectedItemIds?.filter(itemId => itemId !== selectedItemId))
  };

  const handleRemoveButtonClicked = async () => {
    const messages = [];
    setRemovalInProgress(true);
    for (let itemId of selectedItemIds || []) {
      try {
        await props.deleteItem(customerId, itemId);
        messages.push(`${messageConfiguration.itemDeletedSuccessfully} o identyfikatorze ${itemId}`);
      } catch (exception) {
        messages.push(`${messageConfiguration.failedToDeleteItem} o identyfikatorze ${itemId}`);
      }
    }
    props.onCleanupConfirmed(messages);
  };

  const determineRemovableItems = (
    items: AuditableNamedIdentifiable[],
    completedOrderItems: CompletedOrderItem[],
    activeOrderedItems: OrderedItem[],
    orderedKitItems: OrderedKitItem[]
  ): RemovableItem[] => {
    const getModificationTime = (itemId: number) => items.find(item => item.id === itemId)?.modificationTime || null;
    const removableItems: Omit<RemovableItem, 'kitNames' | 'orderNumbers'>[] = completedOrderItems.map(orderItem => ({
      id: orderItem.itemId,
      name: orderItem.name,
      latestOrderCompletionDate: orderItem.latestCompletionDate || null,
      isUsedInAnyOrder: true,
      modificationTime: getModificationTime(orderItem.itemId)
    }));
    const removableItemIds = removableItems.map(getId);
    items.forEach(item => {
      const itemId = item.id;
      if (!removableItemIds.includes(itemId)) {
        removableItems.push(({
          id: itemId,
          name: item.name,
          latestOrderCompletionDate: null,
          isUsedInAnyOrder: false,
          modificationTime: item.modificationTime || null
        }));
      }
    });
    removableItems.forEach(item => console.info(JSON.stringify(item)));
    const activeOrderItemIds = activeOrderedItems.map(orderedItem => getId(orderedItem.item));
    return removableItems
      .filter(item => latestOrderCompletionDate === undefined
          || (item.latestOrderCompletionDate !== undefined && item.latestOrderCompletionDate !== null && item.latestOrderCompletionDate <= latestOrderCompletionDate)
          || !item.isUsedInAnyOrder)
      .filter(item => latestItemModificationDate === undefined
          || item.modificationTime === null
          || formatDate(item.modificationTime) <= latestItemModificationDate)
      .filter(item => !activeOrderItemIds.includes(item.id))
      .map(item => enrichWithKitNamesAndOrderNumbers(item, orderedKitItems));
  };

  const enrichWithKitNamesAndOrderNumbers = (
    item: Omit<RemovableItem, 'kitNames' | 'orderNumbers'>,
    orderedKitItems: OrderedKitItem[]
  ): RemovableItem => {
    const kitNames: string[] = [];
    const orderNumbers: string[] = [];
    orderedKitItems
      .filter(orderedKitItem => orderedKitItem.item.id === item.id)
      .forEach(orderedKitItem => {
        const kitName = orderedKitItem.kit.name;
        if (!kitNames.includes(kitName)) {
          kitNames.push(kitName);
        }
        const order = orderedKitItem.order;
        if (order !== undefined) {
          if (order.status !== OrderStatus.Complete) {
            const orderNumber = order.number;
            if (!orderNumbers.includes(orderNumber)) {
              orderNumbers.push(orderNumber);
            }
          }
        }
      });
    return { ...item, kitNames, orderNumbers };
  };
  
  const hasDependencies = (item: RemovableItem) =>
    item.kitNames.length === 0 && item.orderNumbers.length === 0;

  const getOrderItemUtilizationMessage = (item: RemovableItem) => {
    const kitNames = item.kitNames
    const kitUtilizationMessage = kitNames.length === 0
      ? `nie ${messageConfiguration.used} w kompletach`
      : `${messageConfiguration.used} w następujących kompletach: ${kitNames.join(', ')}`;
    const orderNumbers = item.orderNumbers;
    return orderNumbers.length === 0
      ? kitUtilizationMessage
      : `${kitUtilizationMessage}; pośrednio ${messageConfiguration.used} w aktywnych zamówieniach: ${item.orderNumbers.join(', ')}`;
  };

  useEffect(() => {
    setRemovalInProgress(false);
    if (props.open === true) {
      props.fetchItems(customerId)
        .then(setItems);
      props.fetchCompletedOrderItems(customerId)
        .then(setCompletedOrderItems);
      props.fetchActiveOrderedItems(customerId)
        .then(setActiveOrderedItems);
      props.fetchActiveOrderedKitItems(customerId)
        .then(setActiveOrderedKitItems);
    }
  }, [props.open]);

  useEffect(() => {
    if (items !== undefined && completedOrderItems !== undefined && activeOrderedItems !== undefined && activeOrderedKitItems != undefined) {
      const removableItems = determineRemovableItems(items, completedOrderItems, activeOrderedItems, activeOrderedKitItems);
      setRemovableItems(removableItems);
      setSelectedItemIds(removableItems
        .filter(hasDependencies)
        .map(getId));
    }
  }, [items, completedOrderItems, activeOrderedItems, activeOrderedKitItems, latestItemModificationDate, latestOrderCompletionDate]);

  return (
    <Dialog open={ props.open } maxWidth={ false }>
      <Box className={ classes.dialogContent }>
        <DateInputField
          initialValue={ getCurrentDate() }
          label='Data realizacji najnowszego zamówienia'
          onValueChange={ setLatestOrderCompletionDate }
        />
        <DateInputField
          initialValue={ getCurrentDate() }
          label='Ostatnia data modyfikacji'
          onValueChange={ setLatestItemModificationDate }
        />
        { removableItems === undefined
          ? <div>...</div>
          : removableItems
              .map(item =>
                <Tooltip title={ getOrderItemUtilizationMessage(item) }>
                  <FormControlLabel
                    label={ `${item.name}
                      | data ostatniej modyfikacji: ${item.modificationTime === null
                          ? `nieznana (przed ${MINIMUM_MODIFICATION_DATE})`
                          : formatDate(item.modificationTime)}
                      | ${item.isUsedInAnyOrder
                          ? `ostatnia data zamówienia: ${item.latestOrderCompletionDate}`
                          : `nie ${messageConfiguration.used} bezpośrednio w zamówieniach`}` }
                    control={ <Checkbox
                      checked={ selectedItemIds === undefined ? false : selectedItemIds.includes(item.id) }
                      onChange={ (event, checked) => handleItemSelected(item.id, checked) }
                    /> }
                    disabled={ !hasDependencies(item) }
                  />
                </Tooltip>)
        }
      </Box>
      <Button
        color='primary'
        className={ classes.button }
        onClick={ handleRemoveButtonClicked }
        variant='contained'
        disabled={ isRemovalInProgress }
      >
          Usuń { isRemovalInProgress && <CircularProgress size={ 24 } /> }
      </Button>
      <Button
        color='secondary'
        className={ classes.button }
        onClick={ props.onCleanupCanceled }
        variant='contained'
      >
          Anuluj
      </Button>
    </Dialog>
  );
}

export default OrderItemCleanupDialog;